import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { EstablishmentService } from '../../../services/establishment/establishment.service';
import { AdvanceSearchService } from '../../../services/Advance-search/advance-search.service';
import { ToastService } from '../../../services/toast/toast.service';
import { LoaderComponent } from '../../../atoms/loader/loader.component';
import { PincodeLookupController } from '../../../common-files/pincode-lookup';

// add or edit a property. data.property = the row when editing, null when adding.
// utsav assignment is NOT done here — use "Assign to Utsav" on the Mandir tab.
// self-saving: closes with `true` when a change was persisted so the parent refreshes.
@Component({
  selector: 'app-property-form-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    LoaderComponent,
  ],
  templateUrl: './property-form-dialog.component.html',
  styleUrls: ['./property-form-dialog.component.scss', '../../../../common-style/ac-dialog.scss'],
})
export class PropertyFormDialogComponent implements OnInit, OnDestroy {
  form: FormGroup;
  isEdit: boolean = false;
  isLoading: boolean = false;
  readonly pincodeLookup: PincodeLookupController;
  // INTERNAL = on the mandir campus, EXTERNAL = rented / outside building
  readonly categoryOptions = [
    { value: 'INTERNAL', label: 'Internal (on campus)' },
    { value: 'EXTERNAL', label: 'External (outside)' },
  ];

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<PropertyFormDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { property: any | null },
    private establishmentService: EstablishmentService,
    private toastService: ToastService,
    private advanceSearchService: AdvanceSearchService
  ) {
    this.form = this.fb.group({
      name: ['', Validators.required],
      category: ['INTERNAL', Validators.required],
      address: [''],
      state: [''],
      district: [''],
      pincode: ['', Validators.pattern(/^\d{6}$/)],
      landmark: [''],
      locationUrl: [''],
      latitude: [null as number | null, [Validators.min(-90), Validators.max(90)]],
      longitude: [null as number | null, [Validators.min(-180), Validators.max(180)]],
      contact: ['', Validators.pattern(/^\d{10}$/)],
      altContact: ['', Validators.pattern(/^\d{10}$/)],
    });
    this.pincodeLookup = new PincodeLookupController(
      this.form,
      this.advanceSearchService
    );
  }

  ngOnInit(): void {
    if (this.data?.property) {
      this.isEdit = true;
      const p = this.data.property;
      this.form.patchValue({
        name: p.name || '',
        category: p.category || 'INTERNAL',
        address: p.address || '',
        state: p.state || '',
        district: p.district || '',
        pincode: p.pincode || '',
        landmark: p.landmark || '',
        locationUrl: p.locationUrl || '',
        latitude: p.latitude ?? null,
        longitude: p.longitude ?? null,
        contact: p.contact || '',
        altContact: p.altContact || '',
      }, { emitEvent: false });
    }

    this.pincodeLookup.start();
  }

  ngOnDestroy(): void {
    this.pincodeLookup.destroy();
  }

  get pincodeControl() {
    return this.pincodeLookup.pincodeControl;
  }

  get isPincodeLoading(): boolean {
    return this.pincodeLookup.isLoading;
  }

  get pincodeLookupStatus() {
    return this.pincodeLookup.status;
  }

  get pincodeLookupMessage(): string {
    return this.pincodeLookup.message;
  }

  get stateList(): string[] {
    return this.pincodeLookup.stateList;
  }

  get districtList(): string[] {
    return this.pincodeLookup.districtList;
  }

  onDistrictChange(): void {
    this.pincodeLookup.resolveSelectedDistrict();
  }

  submit(): void {
    const pincode = String(this.form.get('pincode')?.value ?? '');
    if (!this.pincodeLookup.isVerified(pincode)) {
      this.form.get('pincode')?.markAsTouched();
      this.toastService.error(
        this.pincodeLookupMessage || 'Enter a valid, verified 6-digit pincode.',
      );
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toastService.error('Please complete the required property details.');
      return;
    }

    // coordinates are optional but must come as a pair.
    const latitude = this.form.get('latitude')?.value;
    const longitude = this.form.get('longitude')?.value;
    const hasLatitude = latitude !== null && latitude !== undefined && `${latitude}` !== '';
    const hasLongitude = longitude !== null && longitude !== undefined && `${longitude}` !== '';
    if (hasLatitude !== hasLongitude) {
      this.toastService.error('Latitude and Longitude must be provided together.');
      return;
    }

    this.isLoading = true;
    const payload = this.form.getRawValue();
    const request$ = this.isEdit
      ? this.establishmentService.updateProperty(this.data.property.propertyId, payload)
      : this.establishmentService.addProperty(payload);

    request$.subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Property saved.');
        this.isLoading = false;
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to save property.');
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
